import css from './Details.module.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { CurrencyCode, CurrencySymbol } from 'meta/currency'
import DataTestId from 'meta/dataTestId'
import { IUserTicketData } from 'meta/pages/v4/ticket'
import { IWalletUserTicketDetails, IWalletUserTicketPersonalData, IWalletUserTicketPrice } from 'meta/pages/wallet'
import { convertAndFormat, getCodeFromSymbol } from 'helpers/currency/converter'
import { Dates } from 'utils/dates'

import EventDataFetcher from 'components/Pages/Wallet/BulkDataFetcher/TicketDataFetcher/EventDataFetcher'
import { useActiveCurrency } from 'hooks/useActiveCurrency'
import { useCurrentLanguage } from 'hooks/useCurrentLanguage'

import SeasonEventDetails from './SeasonEventDetails'

type Props = {
  show: boolean
  ticketDetails: IWalletUserTicketDetails
  ticketData: IUserTicketData
}

interface IWalletDetailsField {
  key: string
  dateFormat?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parser?: (object: any, property: string, locale: string, currency: CurrencyCode) => string
}

const TicketCardDetails: React.FC<Props> = (props) => {
  const { ticketData, ticketDetails, show } = props

  const { t } = useTranslation()

  const locale = useCurrentLanguage()

  const { activeCurrency } = useActiveCurrency()

  const [personalData, setPersonalData] = useState<IWalletUserTicketPersonalData | null>(null)
  const [fetchEventData, setFetchEventData] = useState<boolean>(false)

  const isBothSIAEandFxTT =
    Boolean(ticketData.fiscalData.receiptUri) && Boolean(ticketData.fiscalData.emission?.fiscalSeal)

  const formatPrice = (
    object: IWalletUserTicketDetails,
    property: string,

    // @ts-expect-error
    locale: string,
    activeCurrency: CurrencyCode = CurrencyCode.NONE
  ): string => {
    // @ts-expect-error
    const { amount, symbol, formatted } = object[property] as IWalletUserTicketPrice

    const eventCurrencyCode = getCodeFromSymbol(symbol as CurrencySymbol)

    // @ts-expect-error
    const convertedPrice = ticketDetails['zki']
      ? formatted
      : convertAndFormat(Number(amount) / 100, eventCurrencyCode, activeCurrency)

    return convertedPrice
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatSeasonTicket = (_object: boolean): string => {
    return ''
  }

  const formatDate =
    (dateFormat: string) =>
    (object: IWalletUserTicketDetails, property: string): string => {
      // @ts-expect-error
      return Dates.format(new Date(object[property]), dateFormat)
    }

  const fields: Array<IWalletDetailsField> = [
    {
      key: 'eventName',
    },
    {
      key: 'dateEvent',
      dateFormat: 'dates:wallet:dateEvent',
    },
    {
      key: 'locationName',
    },
    {
      key: 'openDateEvent',
      dateFormat: 'dates:wallet:dateOpening',
    },
    {
      key: 'buyerName',
    },
    {
      key: 'phone',
    },
    {
      key: 'organizerCompanyName',
    },
    {
      key: 'orderCodeDescription',
    },
    {
      key: 'ticketDescription',
    },
    {
      key: 'seasonCode',
    },
    // {
    //   key: 'isSeasonTicket',
    //   parser: formatSeasonTicket,
    // },
    {
      key: 'seasonTicketCode',
    },
    {
      key: 'seasonProgressive',
    },
    {
      key: 'numberOfEntrances',
    },
    {
      key: 'endDate_tz',
      dateFormat: 'dates:wallet:dateEvent',
    },
    {
      key: 'emissionDate',
      dateFormat: 'dates:wallet:dateEmission',
    },
    {
      key: 'emissionSystem',
    },
    {
      key: 'fiscalSeal',
    },
    {
      key: 'fiscalProgressive',
    },
    {
      key: 'activationCard',
    },
    {
      key: 'zki',
    },
    {
      key: 'jir',
    },
    {
      key: 'racun',
    },
    {
      key: 'workerOib',
    },
    {
      key: 'pvCode',
    },
    {
      key: 'price',
      parser: formatPrice,
    },
    {
      key: 'presale',
      parser: formatPrice,
    },
    {
      key: 'totalPrice',
      parser: formatPrice,
    },
    {
      key: 'statusLabel',
    },
    {
      key: 'vatPermit',
    },
    {
      key: 'exoNumber',
    },
  ]

  const goodTicketFieldKeysToRename = ['openDateEvent', 'emissionDate']

  const getFilteredFields = (fields: Array<IWalletDetailsField>): Array<IWalletDetailsField> => {
    return fields
  }

  useEffect(() => {
    if (ticketDetails.personalData) {
      const { gender, dateOfBirth, birthplaceCityName, birthplaceCountry } = ticketDetails.personalData

      const personalDataObj = {
        gender: String(t(`wallet:${gender}`)),
        birthDate: Dates.format(new Date(dateOfBirth), String(t('dates:wallet:dateEvent'))),
        birthplaceCountry: birthplaceCountry.name,
      }

      if (birthplaceCityName) {
        // @ts-expect-error
        personalDataObj['birthplaceCity'] = birthplaceCityName.name
      }

      setPersonalData(personalDataObj)
    }
  }, [])

  useEffect(() => {
    if (show) {
      setFetchEventData(true)
    }
  }, [show])

  return (
    <>
      <div
        className={classNames(
          css.cardDetails,
          { [css.personalData]: personalData },
          // { [css.isSeasonTicket]: ticketDetails && Boolean(ticketDetails['isSeasonTicket']) },
          { [css.isBothSIAEandFxTT]: isBothSIAEandFxTT }
        )}
        data-testid={DataTestId.wallet.ticketDetails}
      >
        {ticketDetails &&
          getFilteredFields(fields).map((field) => {
            // @ts-expect-error
            if (!ticketDetails[field.key] && field.key !== 'ticketDescription') {
              return
            }

            if (ticketDetails['isSeasonTicket'] && ['dateEvent', 'openDateEvent'].includes(field.key)) {
              return
            }

            if (field.key === 'ticketDescription' && Boolean(ticketData.fiscalData.emission?.fiscalSeal)) {
              return (
                <div key={field.key}>
                  <div className={css.cardDetails__key}>{`${t(`wallet:${field.key}`)}:`}</div>
                  <div className={css.cardDetails__value}>{String(ticketDetails['typeTicketDescription']).trim()}</div>
                </div>
              )
            }

            return (
              <div key={field.key}>
                <div className={css.cardDetails__key}>
                  {`${t(`wallet:${goodTicketFieldKeysToRename.includes(field.key) ? `${field.key}_goods` : field.key == 'eventName' && ticketData.seasonData ? 'seasonName' : field.key}`)}${field.key !== 'isSeasonTicket' ? ':' : ''}`}
                </div>
                <div className={css.cardDetails__value} data-testid={DataTestId.wallet.ticketDetailsValue}>
                  {field.dateFormat
                    ? formatDate(String(t(field.dateFormat)))(ticketDetails, field.key)
                    : field.parser
                      ? field.parser(ticketDetails, field.key, locale, activeCurrency)
                      : // @ts-expect-error
                        String(ticketDetails[field.key]).trim()}
                </div>
              </div>
            )
          })}

        {ticketDetails && ticketData.seasonData && ticketDetails['eventTickets'] && (
          <div className={css.eventTickets}>
            {ticketDetails['eventTickets'].map((ticket, key) => {
              return <SeasonEventDetails key={key} eventId={ticket.id} status={ticket.status} />
            })}
          </div>
        )}

        {ticketDetails && ticketData.seasonData && ticketDetails['eventTickets'] && fetchEventData && (
          <EventDataFetcher skipLocation eventIds={ticketDetails['eventTickets'].map((x) => x.id)} />
        )}

        {personalData &&
          Object.keys(personalData).map((name) => (
            <div key={name}>
              <div className={css.cardDetails__key}>{`${t(`wallet:${name}`)}:`}</div>
              {}
              {/* @ts-expect-error */}
              <div className={css.cardDetails__value}>{personalData[name]}</div>
            </div>
          ))}

        {/* {ticketDetails && ticketDetails['invoiceUrl'] && (
          <div
            className={css.printInvoice}
            onClick={() => {
              // eslint-disable-next-line
              // @ts-expect-error
              window.location.href = ticketDetails['invoiceUrl']
            }}
          >
            Print Invoice
          </div>
        )} */}

        {ticketData.fiscalData.receiptUri && (
          <div
            className={classNames(css.showReceipt, { [css.isBothSIAEandFxTT]: isBothSIAEandFxTT })}
            onClick={() => {
              if (ticketData.fiscalData.receiptUri) {
                window.open(ticketData.fiscalData.receiptUri, '_blank')
              }
            }}
          >
            {t('wallet:show_receipt')}
          </div>
        )}

        {
          <div className={css.multiReceiptUri}>
            {ticketData.fiscalData.receiptUris?.map((uri, key) => (
              <div
                className={css.uri}
                key={key}
                onClick={() => {
                  window.open(uri, '_blank')
                }}
              >
                {`${t('wallet:show_receipt')} ${key + 1}`}
              </div>
            ))}

            {ticketData.fiscalData.creditNoteUris?.map((uri, key) => (
              <div
                className={css.uri}
                key={key}
                onClick={() => {
                  window.open(uri, '_blank')
                }}
              >
                {`${t('wallet:show_credit_note')} ${key + 1}`}
              </div>
            ))}
          </div>
        }
      </div>
    </>
  )
}

export default TicketCardDetails
